// Who a task is for, within its roles: store category, store trade, the teams
// of chosen Assistant Managers and Trade Heads, and hand-picked people. The
// server holds the rule (apps/api/src/services/task-audience.service.ts); this
// file only helps the Tasks page show and edit it.
//
// The rule, for wording: a task is assigned to roles. The boxes narrow it down
// within those roles: within one box ANY picked value will do, and the boxes
// that are filled combine as the admin chose — a person must match ALL of
// them, or ANY one of them. Hand-picked people narrow it further: only they
// get the task.

export type AudienceMatch = 'all' | 'any';

export interface TaskAudienceFields {
  target_store_category_ids?: number[] | null;
  target_store_trade_ids?: string[] | null;
  target_am_codes?: string[] | null;
  target_th_codes?: string[] | null;
  target_audience_match?: AudienceMatch | string | null;
  /** Hand-picked people (employee codes); empty = everyone who fits. */
  target_employee_codes?: string[] | null;
}

export interface AudienceOption {
  value: string;
  label: string;
}

export interface AudienceOptions {
  categories: AudienceOption[];
  trades: AudienceOption[];
  ams: AudienceOption[];
  ths: AudienceOption[];
  /**
   * Trades retired from the project's trade list. Not offered for picking, but a
   * task that already names one still enforces it, so it must show by name.
   */
  retiredTrades?: AudienceOption[];
}

export const NO_AUDIENCE_OPTIONS: AudienceOptions = { categories: [], trades: [], ams: [], ths: [] };

/** Values that can no longer be picked but still have a name, per kind of list. */
export const retiredOptionsOf = (options: AudienceOptions, key: keyof AudienceOptions): AudienceOption[] =>
  key === 'trades' ? (options.retiredTrades ?? []).map((o) => ({ ...o, label: `${o.label} (retired)` })) : [];

export type AudienceKind = 'category' | 'trade' | 'am' | 'th';

export interface AudienceChip {
  kind: AudienceKind;
  /** Short text for the list, e.g. "Category: L0" or "2 trades". */
  label: string;
  /** Every picked value, for a tooltip. */
  detail: string;
}

const strings = (values: unknown): string[] =>
  Array.isArray(values) ? values.map((v) => String(v ?? '').trim()).filter(Boolean) : [];

/** The picked values of each kind, as the strings the pickers work with. */
export const audienceValues = (task: TaskAudienceFields | null | undefined): Record<AudienceKind, string[]> => ({
  category: strings(task?.target_store_category_ids),
  trade: strings(task?.target_store_trade_ids).map((v) => v.toLowerCase()),
  am: strings(task?.target_am_codes),
  th: strings(task?.target_th_codes),
});

/** How the task's filled boxes combine. Anything but 'any' is 'all', as on the server. */
export const audienceMatchOf = (task: TaskAudienceFields | null | undefined): AudienceMatch =>
  task?.target_audience_match === 'any' ? 'any' : 'all';

/** The task's hand-picked people, as employee codes. */
export const pickedEmployeesOf = (task: TaskAudienceFields | null | undefined): string[] => strings(task?.target_employee_codes);

/** "All of" / "Any of" for a task with two or more boxes filled; '' when the choice makes no difference. */
export const audienceJoinLabel = (task: TaskAudienceFields | null | undefined): '' | 'All of' | 'Any of' =>
  countAudienceKinds(task) < 2 ? '' : audienceMatchOf(task) === 'any' ? 'Any of' : 'All of';

/** How many kinds of condition the task sets (0–4). */
export const countAudienceKinds = (task: TaskAudienceFields | null | undefined): number =>
  Object.values(audienceValues(task)).filter((values) => values.length > 0).length;

const KINDS: Array<{
  kind: AudienceKind;
  options: keyof AudienceOptions;
  one: (name: string) => string;
  many: (n: number) => string;
}> = [
  { kind: 'category', options: 'categories', one: (name) => `Category: ${name}`, many: (n) => `${n} categories` },
  { kind: 'trade', options: 'trades', one: (name) => `Trade: ${name}`, many: (n) => `${n} trades` },
  { kind: 'am', options: 'ams', one: (name) => `Team of ${name}`, many: (n) => `Teams of ${n} AMs` },
  { kind: 'th', options: 'ths', one: (name) => `Team of ${name}`, many: (n) => `Teams of ${n} Trade Heads` },
];

const labelOf = (options: AudienceOption[], value: string): string =>
  options.find((o) => o.value.toLowerCase() === value.toLowerCase())?.label ?? value;

/** One chip per kind of condition the task sets, in a fixed order. Empty for a task with none. */
export const describeTaskAudience = (
  task: TaskAudienceFields | null | undefined,
  options: AudienceOptions,
): AudienceChip[] => {
  const values = audienceValues(task);
  return KINDS.flatMap(({ kind, options: key, one, many }) => {
    const picked = values[kind];
    if (picked.length === 0) return [];
    const known = [...(options[key] ?? []), ...retiredOptionsOf(options, key)];
    const names = picked.map((value) => labelOf(known, value));
    return [{ kind, label: names.length === 1 ? one(names[0]) : many(names.length), detail: names.join(', ') }];
  });
};

/**
 * The picker's options plus any picked value that is not among them, so it stays
 * visible and removable instead of hiding inside the saved task. A picked value
 * with a known name (a retired trade) shows by that name; one with none shows its
 * raw value, marked "(no longer listed)" — unless `listIsComplete` is false (the
 * list is still loading or failed to load), when we cannot say that it is gone.
 * Picked extras go FIRST: a long list is only drawn in part.
 */
export const optionsWithPicked = (
  options: AudienceOption[],
  picked: string[],
  retired: AudienceOption[] = [],
  listIsComplete = true,
): AudienceOption[] => {
  const known = new Set(options.map((o) => o.value.toLowerCase()));
  const extras = picked
    .filter((value) => !known.has(value.toLowerCase()))
    .map((value) => {
      const named = retired.find((o) => o.value.toLowerCase() === value.toLowerCase());
      if (named) return { value, label: named.label };
      return { value, label: listIsComplete ? `${value} (no longer listed)` : value };
    });
  return [...extras, ...options];
};

/** What to send to the server for the picked values. */
export const audienceFieldsFrom = (values: Record<AudienceKind, string[]>): Required<{
  target_store_category_ids: number[];
  target_store_trade_ids: string[];
  target_am_codes: string[];
  target_th_codes: string[];
}> => ({
  target_store_category_ids: values.category.map((v) => Number(v)).filter((n) => Number.isInteger(n) && n > 0),
  target_store_trade_ids: values.trade,
  target_am_codes: values.am,
  target_th_codes: values.th,
});
